import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Target, Heart, CheckCircle } from 'lucide-react';
import { doctorInfo, stats } from '../mockData';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-cyan-600 via-sky-500 to-blue-600 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              About V Derma
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Your trusted partner in achieving healthy, radiant skin through expert care and advanced treatments
            </p>
          </motion.div>
        </div>
      </section>

      {/* Doctor Profile */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={doctorInfo.image}
                    alt={doctorInfo.name}
                    className="w-full h-[680px] object-cover"
                  />
                </div>
                <motion.div
                  className="absolute -bottom-6 -right-6 bg-gradient-to-br from-cyan-600 to-blue-600 p-6 rounded-2xl shadow-2xl"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  <p className="text-white text-3xl font-bold">{doctorInfo.experience}</p>
                  <p className="text-white/90 text-sm">Experience</p>
                </motion.div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {doctorInfo.name}
              </h2>
              <p className="text-cyan-600 font-semibold text-xl mb-6">
                {doctorInfo.qualification}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                {doctorInfo.about}
              </p>

              <div className="space-y-4">
                {doctorInfo.achievements.map((achievement, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-1" />
                    <p className="text-gray-700">{achievement}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <p className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Core Values</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: 'Patient Care',
                description: 'Your health and comfort are our top priorities'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'Committed to delivering the highest quality treatments'
              },
              {
                icon: Users,
                title: 'Trust',
                description: 'Building lasting relationships through transparency'
              },
              {
                icon: Target,
                title: 'Results',
                description: 'Focused on achieving your skin health goals'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-2xl text-center hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <value.icon className="w-16 h-16 text-cyan-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600 via-sky-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            {...fadeInUp}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              To provide comprehensive, evidence-based dermatological care that empowers individuals to achieve their healthiest skin. We combine advanced technology with compassionate care to deliver treatments that truly make a difference in our patients' lives.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
